// SINGLE SOURCE OF TRUTH for the whole app.
// A producer edits this file before deployment. There is no in-app editor.
// No tool name, organization name, date, or area label is hard-coded anywhere
// else in the codebase, every screen reads from here.

export const config = {
  event_name: "AI Lab",
  event_subtitle: "",
  event_date: "2026-09-10",
  timezone: "America/New_York",
  organizations: [],
  area_labels: {
    prompt_school: "Prompt School",
    build: "The Build",
    mentors: "Mentor Resources",
    stack: "The Sustainable Stack",
    feedback: "Feedback",
  },
  areas: [
    { id: "mentors", order: 0, always_available: true, timers: [] },
    {
      id: "prompt_school",
      order: 1,
      comes_after: "the AI expert demo and tool setup",
      timers: [{ label: "Prompt School", duration_minutes: 30 }],
    },
    {
      id: "build",
      order: 2,
      comes_after: "Prompt School",
      // Two timers, not one. Ten minutes to decide and forty-five to build is
      // the discipline this segment depends on. A single 55 minute timer would hide it.
      timers: [
        { label: "Scoping", duration_minutes: 10 },
        { label: "Building", duration_minutes: 45 },
      ],
    },
    {
      id: "stack",
      order: 3,
      comes_after: "Show and Tell",
      timers: [],
    },
    {
      id: "feedback",
      order: 4,
      always_available: true,
      timers: [],
    },
  ],
  tools: {
    assistant: {
      name: "Claude",
      url: "https://claude.ai",
      project_noun: "Project",
    },
    workspaces: [
      {
        name: "Google Workspace",
        build_surfaces: [
          "Google Apps Script",
          "Google Forms",
          "Google Sheets",
          "Gmail templates and filters",
        ],
      },
      {
        name: "Microsoft 365",
        build_surfaces: [
          "Power Automate",
          "Outlook rules",
          "Microsoft Forms",
          "Excel with Office Scripts",
        ],
      },
    ],
    optional: [
      {
        name: "PartyRock",
        url: "https://partyrock.aws",
        note: "Only if a team wants a shareable web app for colleagues who will not use the assistant directly. Not the default.",
      },
    ],
  },
  data_rule:
    "No real names, contact details, student or family records, donor records, payroll data, or attendance sheets go into any AI tool today. All work is done on structure and made-up example data.",
  verification: {
    region: "US",
  },
  sponsored_access: null,
  retention_hours: 48,

  // Per-step content. Each step carries both Right Now bar variants so a
  // producer can edit them without touching code. A step may omit one variant.
  content: {
    mentors: {
      steps: [
        {
          title: "Mentor Resources",
          right_now: {
            volunteer:
              "Read this before the room starts and whenever the app is not running.",
          },
        },
      ],
    },
    prompt_school: {
      steps: [
        {
          title: "Meet R-T-C-F",
          right_now: {
            nonprofit: "Read the four elements once. You will use them next.",
            volunteer: "Let them read first. Do not explain yet.",
          },
        },
        {
          title: "The scored challenge",
          right_now: {
            nonprofit: "Fill in the four boxes for a task you actually do.",
            volunteer: "Ask which of the four elements is thinnest. Do not type.",
          },
        },
        {
          title: "Choose your challenge",
          right_now: {
            nonprofit:
              "Pick the challenge that matches real work you have this month.",
            volunteer: "Help them pick one real challenge. Do not type.",
          },
        },
        {
          title: "Reflect",
          right_now: {
            nonprofit: "Answer the three questions before you save.",
            volunteer: "Ask the three questions out loud. Then listen.",
          },
        },
      ],
    },
    build: {
      steps: [
        {
          title: "Scope it",
          right_now: {
            nonprofit: "Say what you want in one sentence, then pick a recipe.",
            volunteer: "Help them narrow to one job. Do not type.",
          },
        },
        {
          title: "Build it",
          right_now: {
            nonprofit: "Follow the recipe steps on your screen.",
            volunteer:
              "Watch for stuck moments. Coach, do not take the keyboard.",
          },
        },
        {
          title: "Peer review",
          right_now: {
            nonprofit: "Show it to another table. Apply one fix, not five.",
            volunteer: "Help them hear the feedback and pick one change.",
          },
        },
        {
          title: "Save and wrap",
          right_now: {
            nonprofit: "Say what it is good for and the one thing you'll try.",
            volunteer: "Note Taker: bank it into their Project before you close.",
          },
        },
      ],
    },
    stack: {
      steps: [
        {
          title: "Take it home",
          right_now: {
            nonprofit: "Note one tool your team will actually keep using.",
            volunteer: "Help them name a steward for each tool.",
          },
        },
      ],
    },
  },

  // Segment guide: a precise ordered map of each timed segment. Each item maps
  // to a navigable step index. Minutes are guidance only. Build items also
  // carry the timer they sit under (Scoping or Building).
  segments: {
    prompt_school: [
      {
        name: "Learn the framework",
        minutes: 5,
        step: 0,
        line: "The four R-T-C-F cards, with one worked example each.",
      },
      {
        name: "Write one prompt and score it",
        minutes: 10,
        step: 1,
        line: "Four boxes for a task you actually do, scored on your own device, fix the weakest element, then run it.",
      },
      {
        name: "Pick a challenge",
        minutes: 2,
        step: 2,
        line: "Six options, alone with your coaches or as one organization team.",
      },
      {
        name: "Work the challenge",
        minutes: 10,
        step: 2,
        line: "You type, coaches guide, save as you go.",
      },
      {
        name: "Reflect and save",
        minutes: 3,
        step: 3,
        line: "Three questions, then save everything into your Project.",
      },
    ],
    build: [
      {
        name: "Write the scoping sentence",
        minutes: 5,
        step: 0,
        timer: "Scoping",
        line: '"We are building blank, for blank, and done looks like blank." Say it out loud before you pick.',
      },
      {
        name: "Pick your recipe",
        minutes: 5,
        step: 0,
        timer: "Scoping",
        line: "Browse all nine and talk about which one fits, or use the problem table.",
      },
      {
        name: "Work your recipe",
        minutes: 30,
        step: 1,
        timer: "Building",
        line: "Step-by-step build, one step at a time.",
      },
      {
        name: "Peer review",
        minutes: 8,
        step: 2,
        timer: "Building",
        line: "Swap with a table from the other organization. Two comments each, apply one fix.",
      },
      {
        name: "Save and wrap",
        minutes: 7,
        step: 3,
        timer: "Building",
        line: "Save everything, write what it is good for, pick your presenter.",
      },
    ],
  },

  // Nonprofit feedback page. Swap the Typeform per event without touching code.
  feedback: {
    form_id: "01M3PZ3J0G9CJFNF2N9522Y5RJ",
    form_url: "",
    eyebrow: "FOR NONPROFIT PARTICIPANTS",
    title: "Tell us how that went",
    intro: [
      "This goes to the team who designed today's session. It takes about three minutes.",
      "We use it to decide what changes next time: what to cut, what to spend longer on, and which of these recipes actually turned into something you used after the day.",
      "Be blunt. The most useful thing you can tell us is what did not work.",
    ],
    volunteer_note:
      "Volunteers, this one is not for you. Your feedback comes by email after the event.",
    fallback:
      "Form not loading? That is usually the wifi. You can open it in a new tab instead, or fill it in from the link we email you afterwards.",
  },

  // R-T-C-F teaching content. Examples are localisable per event.
  rtcf: {
    elements: [
      {
        letter: "R",
        name: "Role",
        definition: "who you want the AI to be.",
        example:
          "You are a grants officer who has reviewed education proposals for a city funder for ten years.",
      },
      {
        letter: "T",
        name: "Task",
        definition: "the one thing you want it to do.",
        example:
          "Draft the 'expected outcomes' section of our proposal, under 200 words.",
      },
      {
        letter: "C",
        name: "Context",
        definition: "what it needs to know about you that it cannot guess.",
        example:
          "We run after-school programs for 400 students in grades 3 to 8 across six schools. The funder is a city education agency. Tone: warm but evidence-led.",
      },
      {
        letter: "F",
        name: "Format",
        definition: "the shape of the answer you want back.",
        example:
          "Three short paragraphs, no jargon, ending with one measurable target.",
      },
    ],
    missing_c_line:
      "Almost every disappointing output is a missing C. You knew the context. You just did not type it.",
  },

  // Six real-work challenges for Prompt School part 3. Fields, R-T-C-F seeds,
  // scope promises and stuck panels are all editable here.
  challenges: [
    {
      id: "policy",
      title: "Responsible AI use policy for your organization.",
      description: "Draft one, or review the one you have.",
      variants: [
        {
          id: "draft",
          label: "We do not have a policy yet",
          fields: [
            { key: "org_mission", label: "Organization name and mission" },
            {
              key: "jurisdiction",
              label: "Jurisdiction and applicable laws",
              helper:
                "{verification.region}, name the state and federal rules that apply to you.",
            },
            { key: "serve", label: "Who you serve" },
            { key: "ai_tools", label: "AI tools in use or planned" },
            {
              key: "data_types",
              label: "Types of data you handle",
              helper: "types only, no actual data",
            },
            { key: "red_lines", label: "Non-negotiable red lines" },
          ],
          rtcf: {
            role: "You are a nonprofit operations lead who writes clear internal policies.",
            task: "Draft a short, practical responsible-AI-use policy for our staff and volunteers.",
            format:
              "A one-page policy: purpose, allowed uses, red lines as bullets, and who to ask. Plain language, no legalese.",
          },
          scope: {
            win: "A rough one-page policy: purpose down and a few red lines named, ready to finish later.",
            partial: "Even a purpose statement, or a couple of red lines, is a real start.",
          },
          stuck:
            "Start with the red lines, the three things AI must never touch, and build outward.",
        },
        {
          id: "review",
          label: "We have one already",
          fields: [
            {
              key: "existing_policy",
              label: "Your current policy",
              helper: "paste a made-up example, not the real one",
            },
            { key: "concerns", label: "What you are unsure about" },
            { key: "serve", label: "Who you serve" },
          ],
          rtcf: {
            role: "You are a policy reviewer who finds gaps without rewriting everything.",
            task: "Review our responsible-AI-use policy and flag gaps and risks.",
            format:
              "A short, prioritized list of gaps, each with why it matters and a suggested fix.",
          },
          scope: {
            win: "A first pass at the biggest gaps in your current policy.",
            partial: "Even one or two gaps named clearly is a win.",
          },
          stuck:
            "Check three things first: data red lines, who approves AI use, and what you disclose to families.",
        },
      ],
    },
    {
      id: "grant",
      title: "Grant paragraph rewrite.",
      description:
        "An old proposal section plus a new funder's criteria, keeping your voice.",
      fields: [
        {
          key: "old_section",
          label: "The old proposal paragraph",
          helper: "paste a made-up example",
        },
        { key: "funder_criteria", label: "The new funder's criteria" },
        { key: "voice", label: "Your voice in a few words", helper: "e.g. warm, plain, evidence-led" },
      ],
      rtcf: {
        role: "You are a grant writer who preserves an organization's authentic voice.",
        task: "Rewrite this proposal paragraph to meet the new funder's criteria.",
        format: "One tightened paragraph, same length, in our voice.",
      },
      scope: {
        win: "A rewritten opening you could build on, maybe not the whole paragraph yet.",
        partial: "Even one sentence that hits the funder's priority is progress.",
      },
      stuck: "Name the one funder priority the old paragraph misses, and start there.",
    },
    {
      id: "update",
      title: "Funder progress update.",
      description:
        "Program numbers and one story, written against your stated outcomes.",
      fields: [
        { key: "outcomes", label: "Your stated outcomes" },
        { key: "numbers", label: "Program numbers", helper: "made-up examples only" },
        { key: "story", label: "One short story", helper: "made-up, no real names" },
      ],
      rtcf: {
        role: "You are a program director reporting honestly to a funder.",
        task: "Write a progress update against our stated outcomes.",
        format:
          "Three short paragraphs: the numbers, one story, and next steps. No jargon.",
      },
      scope: {
        win: "A rough progress-update draft against one or two of your outcomes.",
        partial: "Even the numbers paragraph, written against a single outcome, is a win.",
      },
      stuck: "Pair each number with the outcome it proves. Cut numbers that prove nothing.",
    },
    {
      id: "audiences",
      title: "One message, four audiences.",
      description: "Board, funder, families, and a second language your community uses.",
      fields: [
        { key: "core_message", label: "The core message" },
        { key: "specifics", label: "Any specifics", helper: "made-up examples only" },
      ],
      rtcf: {
        role: "You are a communications lead who tailors one message to different readers.",
        task: "Adapt this single message for a board, a funder, families, and in a second language your community uses.",
        format: "Four short versions, clearly labelled. Keep the facts identical.",
      },
      scope: {
        win: "Two or three solid versions of one message, the rest roughed in.",
        partial: "Even the board and family versions done well is a win.",
      },
      stuck: "Write the family version first in plain words, then formalise for the board.",
    },
    {
      id: "monthend",
      title: "Month-end request template.",
      description:
        "Turn the email you chase people with into one that gets complete submissions.",
      fields: [
        {
          key: "current_email",
          label: "The email you currently send",
          helper: "paste a made-up example",
        },
        { key: "complete", label: "What a complete submission includes" },
        { key: "deadline", label: "The deadline" },
      ],
      rtcf: {
        role: "You are an operations coordinator who writes emails people actually complete.",
        task: "Rewrite this month-end request so it gets complete submissions the first time.",
        format:
          "A short email with a clear subject, a numbered checklist, and one deadline.",
      },
      scope: {
        win: "A first draft of a request template you can tighten later.",
        partial: "Even a clear checklist of what 'complete' means is a win.",
      },
      stuck:
        "List exactly what a complete submission includes, then build the email around that list.",
    },
    {
      id: "session",
      title: "Session or training plan.",
      description:
        "A workshop, lesson or staff training outline built from your own standards.",
      fields: [
        { key: "audience", label: "Who it is for" },
        { key: "standards", label: "Your standards or goals" },
        { key: "length", label: "How long the session is" },
      ],
      rtcf: {
        role: "You are a trainer who builds sessions from an organization's own standards.",
        task: "Outline a session, lesson, or staff training from our standards.",
        format:
          "A timed outline with objectives, activities, and a check for understanding.",
      },
      scope: {
        win: "A rough session outline: objectives and a couple of activities down.",
        partial: "Even objectives plus one activity is a win.",
      },
      stuck:
        "Start from the one thing they must be able to do at the end, and work backward.",
    },
  ],

  // Prompt School part 4 reflection.
  reflection: {
    questions: [
      "Which of the four elements made the biggest difference when you changed it?",
      "What did the AI assume about your organization that was not true?",
      "What would embarrass you if your leadership read this output?",
    ],
    save_line: "Save it into your {assistant.name} {assistant.project_noun}.",
  },

  // The Build recipes and the deterministic (no-model) matcher.
  recipes: [
    { id: "grant-proposal-drafting", name: "Grant and proposal drafting assistant" },
    { id: "funder-report", name: "Funder report and impact update assistant" },
    { id: "recurring-document", name: "Recurring document assistant" },
    { id: "multi-audience-rewriter", name: "Multi-audience rewriter" },
    { id: "spreadsheet-question", name: "Spreadsheet question assistant" },
    { id: "month-end-pack", name: "Month-end documentation pack" },
    { id: "workflow-automation", name: "Workflow automation in your own workspace" },
    { id: "ai-use-policy", name: "Responsible AI use policy" },
    { id: "organization-knowledge-base", name: "Organization knowledge base" },
  ],
  recipe_matcher: {
    shape_label: "Shape",
    domain_label: "Domain",
    shapes: [
      { id: "document", label: "A document I will reuse" },
      { id: "colleague", label: "Something a colleague can use without me" },
      { id: "sequence", label: "A sequence that runs itself" },
      { id: "data", label: "An answer out of data I already have" },
    ],
    domains: [
      { id: "money", label: "Money and funders" },
      { id: "ops", label: "Operations and admin" },
      { id: "voice", label: "Voice and audience" },
      { id: "people", label: "People and programs" },
      { id: "governance", label: "Governance" },
    ],
    cross: {
      "document:money": "grant-proposal-drafting",
      "document:ops": "month-end-pack",
      "document:voice": "multi-audience-rewriter",
      "document:people": "recurring-document",
      "document:governance": "ai-use-policy",
      "colleague:money": "recurring-document",
      "colleague:ops": "recurring-document",
      "colleague:voice": "recurring-document",
      "colleague:people": "recurring-document",
      "colleague:governance": "ai-use-policy",
      "sequence:money": "workflow-automation",
      "sequence:ops": "workflow-automation",
      "sequence:voice": "workflow-automation",
      "sequence:people": "workflow-automation",
      "sequence:governance": "workflow-automation",
      "data:money": "spreadsheet-question",
      "data:ops": "spreadsheet-question",
      "data:voice": "funder-report",
      "data:people": "funder-report",
      "data:governance": "spreadsheet-question",
    },
    result_heading: "Based on what you wrote, start here",
    show_all: "I will choose my recipe manually",
    table_heading: "Not sure which one? Find the line that sounds like your problem",
    problem_lines: [
      { text: "We are starting a proposal from a blank page and it takes forever", recipe: "grant-proposal-drafting" },
      { text: "Reporting back to funders takes a week and I dread it", recipe: "funder-report" },
      { text: "I write the same kind of document over and over", recipe: "recurring-document" },
      { text: "One message has to reach the board, the funder and families, in two languages", recipe: "multi-audience-rewriter" },
      { text: "We have a spreadsheet nobody fully understands", recipe: "spreadsheet-question" },
      { text: "Chasing people for month-end paperwork is a part-time job", recipe: "month-end-pack" },
      { text: "The same manual sequence eats hours every week", recipe: "workflow-automation" },
      { text: "We do not have rules for any of this yet", recipe: "ai-use-policy" },
      { text: "We are new to all of this, needs are everywhere", recipe: "organization-knowledge-base" },
    ],
    fallback_recipe: "organization-knowledge-base",
    fallback_badge: "Always works. Also the fallback",
  },

  // Recipe documents for the data-driven engine. One reusable screen renders
  // any of these. Seeded with organization-knowledge-base; more to come.
  recipeDocs: {
    "organization-knowledge-base": {
      id: "organization-knowledge-base",
      title: "Organization knowledge base",
      description:
        "A {assistant.name} {assistant.project_noun} that already knows your organization, set up the way you would onboard a new hire.",
      minutes: 15,
      tools: ["{assistant.name}"],
      needs_workspace: false,
      steps: [
        {
          title: "Create the Project",
          participant_action:
            "Open {assistant.name} and create a new {assistant.project_noun}. Name it after your organization. This becomes the shared brain your whole team reuses.",
          participant_example: "Project name: “Your organization, Org Brain”.",
          volunteer_move:
            "Make sure they create a {assistant.project_noun}, not just a chat. The whole point is that it persists.",
          right_now_participant:
            "Create a {assistant.project_noun} and name it after your organization.",
          right_now_volunteer: "Check they made a Project, not a one-off chat.",
          stuck: [
            "No Project button? A normal chat works for today; move it later.",
            "Name it something obvious so colleagues can find it.",
          ],
        },
        {
          title: "Write the mission",
          participant_action:
            "In the {assistant.project_noun} instructions, write one short paragraph: what your organization does and who it serves. Plain language, the way you would tell a new hire on day one.",
          participant_example:
            "We run after-school programs for 400 students in grades 3 to 8 across six schools, helping them stay on grade level and love learning.",
          volunteer_move:
            "Ask them to say it out loud first, then type. Spoken is usually clearer than written.",
          right_now_participant:
            "Write one paragraph: what you do and who you serve.",
          right_now_volunteer:
            "Have them say the mission out loud, then type it.",
          stuck: [
            "Stuck on wording? Start with “We help ___ do ___.”",
            "Keep it to about five sentences.",
          ],
        },
        {
          title: "Add your programs",
          participant_action:
            "List your main programs, one line each, with a short phrase on what each is for.",
          participant_example:
            "After-School Academy: homework help and enrichment after school. Summer Bridge: six-week math and reading catch-up.",
          volunteer_move:
            "Keep them to the top few programs. A knowledge base that lists everything gets used for nothing.",
          right_now_participant: "List your main programs, one line each.",
          right_now_volunteer:
            "Hold them to the top few programs, not all of them.",
          stuck: [
            "Too many? List the three biggest by budget or headcount.",
            "One line each, detail can live elsewhere.",
          ],
        },
        {
          title: "Set tone and standards",
          participant_action:
            "Tell the {assistant.project_noun} how your organization sounds and the standards it must always hold, voice, reading level, and things you never say.",
          participant_example:
            "Tone: warm but evidence-led. Reading level: plain. Never use deficit language about students or families.",
          volunteer_move:
            "Push for the “never” list. Standards are clearer as prohibitions than as aspirations.",
          right_now_participant:
            "Describe your voice and the standards to always hold.",
          right_now_volunteer:
            "Ask for the “never say” list, it is the sharpest standard.",
          stuck: [
            "Unsure of tone? Paste one made-up sentence written in your voice.",
            "Name one thing you never want to see in your writing.",
          ],
        },
        {
          title: "Add three examples",
          participant_action:
            "Add three pieces of your best past work so the {assistant.project_noun} learns your real standard, for example a strong funder letter, a good report, and a clear parent message.",
          participant_example:
            "1) A funder update you were proud of. 2) A board memo. 3) A family newsletter.",
          volunteer_move:
            "Three is enough. Pick range over volume, different types teach more than three of the same.",
          right_now_participant: "Add three examples of your best past work.",
          right_now_volunteer:
            "Steer them to three different types, not three of the same.",
          stuck: [
            "Can't find three? Two strong examples beat five weak ones.",
            "No file handy? Paste the text.",
          ],
          structure_only: {
            participant_action:
              "Do not add real documents today. Instead, describe the shape of three good examples, type, length, and structure, and paste one made-up version with the same fields.",
            participant_example:
              "“A one-page funder update: opening line, three outcome paragraphs with numbers, one story, a closing ask.” Then a made-up version with invented numbers and a made-up program name.",
            volunteer_move:
              "Coach them to capture the structure, not the content. Real files can be added back at the office, outside this room.",
            stuck: [
              "Describe the skeleton: the sections and their order.",
              "Use invented numbers and a made-up program name.",
            ],
          },
        },
        {
          title: "Test it like a new hire",
          participant_action:
            "Ask the {assistant.project_noun} a real question you would give a new colleague, and see if the answer sounds like your organization. Correct it once; the correction sticks.",
          participant_example:
            "Draft a two-line thank-you to a first-time donor in our voice.",
          volunteer_move:
            "Have them judge the output against their standards from the tone step, not against “is it good”.",
          right_now_participant: "Ask it one real question and check the voice.",
          right_now_volunteer:
            "Make them grade the answer against their own standards.",
          stuck: [
            "Off-voice? Tell it exactly what was wrong, once.",
            "Too generic? It is missing an example, add one.",
          ],
        },
      ],
    },
    "grant-proposal-drafting": {
      id: "grant-proposal-drafting",
      title: "Grant and proposal drafting assistant",
      description:
        "Start a new proposal from a blank page. Build the outline and draft the two hardest sections, in your organization's voice.",
      minutes: 26,
      tools: ["{assistant.name}"],
      needs_workspace: false,
      scope_promise:
        "In 25 minutes you will have a section-by-section outline mapped to your funder's requirements, plus two fully drafted sections. Not a finished proposal. The two hardest pages, and a structure for the rest. Both are a win.",
      steps: [
        {
          title: "Name the funder and the ask",
          minutes: 3,
          participant_action:
            "Write down which funder this is for, which of their programs, roughly how much you are asking for, and the deadline. If they publish guidelines or priorities, paste those in too. If you do not have a specific funder in mind, pick a real one you actually plan to approach.",
          participant_example:
            "A city out-of-school-time grant program, expansion funding, about 75,000 dollars, deadline in November. Their stated priority this cycle is programs in the lowest-income districts they serve.",
          volunteer_move:
            "Do not let the table start writing until a real funder is named. A proposal written for nobody in particular is exactly the generic output we are trying to avoid, and it is the fastest way to lose the next 27 minutes.",
          right_now_participant: "Name the funder, the program, the amount, and the deadline.",
          right_now_volunteer: "No real funder named yet? Do not let them start.",
          stuck: ["No funder in mind? Pick a real one you plan to approach.", "Paste their published priorities if they have any."],
        },
        {
          title: "Dump your raw material",
          minutes: 4,
          participant_action:
            "Do not write prose. Bullet points only, and be specific. Who you serve and how many. What the program actually does in a normal week. The problem in your own words, the way you would say it to a colleague rather than to a funder. Two or three numbers you could defend if challenged. One thing your organization does that a similar organization does not.",
          participant_example:
            "400 students, grades 3 to 8, six schools. Monday to Thursday, 3 to 6 pm, homework help then enrichment. Problem: families finish work at 6 but school ends at 3, and the gap is when kids are least safe. 82 percent attendance. Retained 91 percent of students year over year. We hire site coordinators from the neighbourhoods we serve.",
          volunteer_move:
            "This step is the whole recipe. Everything downstream is built from what lands here, and thin material here produces the bland proposal they are afraid of. Push for numbers and for the unguarded version of the problem statement. If they start writing in funder language, stop them and ask how they would say it to a colleague.",
          right_now_participant: "Bullet points only. Numbers, and the problem in your own words.",
          right_now_volunteer: "Push for real numbers and the unguarded problem statement.",
          stuck: ["Writing prose? Stop. Bullets only.", "Slipping into funder language? Ask how you'd say it to a colleague."],
          structure_only: {
            participant_action:
              "If your raw material includes participant names or family details, build a composite instead: invent a name and blend details from several people. Label it clearly as a composite in the proposal. Funders accept composites, and no real family's details go into an AI tool.",
            participant_example:
              "Composite: ‘Maria, a fifth grader’, blending details from several students, labelled in the proposal as a composite.",
            volunteer_move: "Make sure the composite is labelled as one, and that no real family's details go in.",
            stuck: ["Invent a name, blend several real cases.", "Label it a composite in the proposal itself."],
          },
        },
        {
          title: "Make the assistant interrogate you before it writes",
          minutes: 3,
          participant_action:
            "Give it this instruction and nothing more: ‘You are a grants officer who has reviewed education proposals for city funders for ten years. Before you write anything, ask me the five questions you would need answered to write a competitive proposal to this funder. Do not write the proposal yet.’ Then answer its questions in the chat.",
          participant_example:
            "It will usually ask about the evidence base, what happens to the program without this money, and how you measure the outcome you are claiming.",
          volunteer_move:
            "This is the step tables skip because it feels like a detour. It is the opposite. The answers to these five questions are where the specificity comes from, and a proposal drafted without them will read like every other proposal. Make them answer all five properly before moving on.",
          right_now_participant: "Make it ask you five questions first, then answer all five.",
          right_now_volunteer: "Do not let them skip this. Make them answer all five.",
          stuck: ["It started writing anyway? Tell it to ask first, not write.", "Only got three questions? Ask for five."],
        },
        {
          title: "Build the outline",
          minutes: 4,
          participant_action:
            "Write one R-T-C-F prompt asking for a section-by-section outline mapped to the funder's required headings, with a word budget for each section. Role is the grants officer from step 3. Task is the outline only, not the prose. Context is your raw material and your answers from step 3. Format is the funder's own section headings, with a word count beside each.",
          participant_example:
            "Use the funder's headings exactly. One paragraph of guidance under each, plus a target word count. Total 1,500 words.",
          volunteer_move:
            "Hold them to outline only. The temptation is to let it write the whole thing here, and what comes back is four pages of plausible mush that is harder to fix than a blank page.",
          right_now_participant: "Ask for the outline only, mapped to the funder's headings with word counts.",
          right_now_volunteer: "Outline only. Do not let it write the prose yet.",
          stuck: ["It wrote full prose? Ask again for headings and word counts only.", "No funder headings? Use Need, Approach, Outcomes, Budget."],
        },
        {
          title: "Draft your two hardest sections",
          minutes: 7,
          participant_action:
            "Pick the two sections you dread most. Usually the statement of need and the outcomes. Write one R-T-C-F prompt for each, one at a time, using your raw material as Context and the outline entry as Format.",
          participant_example:
            "You are the grants officer from before. Draft the statement of need. Context: the material I gave you plus my answers. Format: 300 words, three paragraphs, no jargon, opening with the gap between 3 pm and 6 pm rather than with a statistic.",
          volunteer_move:
            "One section per prompt, never both at once. And when the first draft comes back, resist fixing it yourself. Ask which R-T-C-F element would change it: nine times out of ten it is Context, because a fact from step 2 did not make it into the prompt.",
          right_now_participant: "Draft your two hardest sections, one prompt each.",
          right_now_volunteer: "One section per prompt. Draft generic? Ask which element to change.",
          stuck: ["Both at once? Split into two prompts.", "Draft too generic? A fact from step 2 is missing from the Context."],
        },
        {
          title: "Check it against yourself",
          minutes: 3,
          participant_action:
            "Three checks, in this order. Is every number in here one you can source? Does it say anything only your organization could say, or could a similar nonprofit have written it? Would your Executive Director recognise the voice as yours? Fix one thing.",
          participant_example:
            "The need section could have been written by any after-school program. Add the 3 pm to 6 pm safety gap that is specific to us.",
          volunteer_move:
            "This is the Output Reviewer's moment. The second question is the important one. If the answer is no, the fix is not better prompting, it is going back to step 2 and finding the specific detail that was left out.",
          right_now_participant: "Three checks: sourced numbers, only-you specifics, your voice. Fix one thing.",
          right_now_volunteer: "Could a similar nonprofit have written it? If yes, go back to step 2.",
          stuck: ["Reads generic? Find the missing detail from step 2.", "A number you can't source? Cut it."],
        },
        {
          title: "Save it so the next one is faster",
          minutes: 2,
          participant_action:
            "Save the five questions, the outline, and both drafted sections into your organization's {assistant.name} {assistant.project_noun}. Next time you start a proposal you begin from the questions and the outline instead of from a blank page.",
          participant_example:
            "Saved to the {assistant.project_noun}: the five questions, the outline, the need and outcomes drafts.",
          volunteer_move:
            "Make sure the five questions get saved, not just the drafts. The questions are the reusable asset. The drafts are for this one funder only.",
          right_now_participant: "Save the questions, the outline, and both drafts into your {assistant.project_noun}.",
          right_now_volunteer: "Make sure the five questions get saved, not just the drafts.",
          stuck: ["No {assistant.project_noun}? A named chat works for today.", "Save the questions first, they are the reusable part."],
        },
      ],
    },
    "funder-report": {
      id: "funder-report",
      title: "Funder report and impact update assistant",
      description: "A progress update where every stated outcome is backed by a number and, where you can, a story.",
      minutes: 28,
      tools: ["{assistant.name}"],
      needs_workspace: false,
      steps: [
        {
          title: "List your outcomes",
          participant_action: "List the outcomes you promised this funder for this grant, in their words.",
          participant_example: "‘85% of students maintain or improve their reading level.’",
          volunteer_move: "Keep them to the outcomes in the grant, not everything the program does.",
          right_now_participant: "List the outcomes you promised this funder.",
          right_now_volunteer: "Hold them to the grant's outcomes, not the whole program.",
          stuck: ["Can't find them? Check the grant agreement's goals.", "Two or three outcomes is plenty."],
        },
        {
          title: "Paste the numbers",
          participant_action: "Paste your aggregate program numbers for the reporting period.",
          participant_example: "‘412 students served; 88% attendance; 79% reading gains.’",
          volunteer_move: "Aggregate only. If a number could identify a child, it does not belong here.",
          right_now_participant: "Paste your aggregate program numbers for the period.",
          right_now_volunteer: "Check every number is aggregate, never individual.",
          stuck: ["Numbers messy? Paste the summary row only.", "Unsure? Round to whole percentages."],
          structure_only: {
            participant_action: "Use invented numbers with the same categories as your real report, same metrics, made-up values.",
            participant_example: "‘~400 served; ~85% attendance; ~80% gains.’ Rounded, invented, same categories.",
            volunteer_move: "Coach them to keep the category names real and the values invented.",
            stuck: ["Round to the nearest ten or five.", "Keep the metric names; change only the values."],
          },
        },
        {
          title: "Write one story",
          participant_action: "Write one short story from this period in your own words, no real names.",
          participant_example: "‘A sixth grader who started below grade level is now reading with the class.’",
          volunteer_move: "Ask for a specific moment, not a general impression. Specific persuades.",
          right_now_participant: "Write one short story from the period, no real names.",
          right_now_volunteer: "Push for one specific moment, not a summary.",
          stuck: ["Too general? Name one change in one person, anonymised.", "Two sentences is enough."],
        },
        {
          title: "Map to outcomes",
          participant_action: "Write an R-T-C-F prompt that maps your numbers and story to each stated outcome.",
          participant_example: "Task: ‘Write an update where each outcome is backed by a number and, where possible, the story.’",
          volunteer_move: "Make sure every outcome gets evidence. An outcome with no number is a gap to flag, not hide.",
          right_now_participant: "Write the prompt mapping numbers and story to each outcome.",
          right_now_volunteer: "Every outcome needs evidence, or an honest gap.",
          stuck: ["An outcome with no data? Say so plainly.", "Keep Format to short paragraphs per outcome."],
        },
        {
          title: "Generate",
          participant_action: "Generate the update and read it outcome by outcome.",
          participant_example: "Three paragraphs, one per outcome, each with its number.",
          volunteer_move: "Have them check the tone, honest, not triumphant.",
          right_now_participant: "Generate, then read it outcome by outcome.",
          right_now_volunteer: "Watch the tone: honest beats triumphant with funders.",
          stuck: ["Sounds boastful? Ask for a plainer tone.", "Missing an outcome? Add it to the Task."],
        },
        {
          title: "Check every number",
          participant_action: "Check every number in the draft against your source. Fix anything the assistant rounded or invented.",
          participant_example: "Confirm ‘88% attendance’ matches your source sheet.",
          volunteer_move: "This is the non-negotiable step. Numbers get checked by hand, every one.",
          right_now_participant: "Check every number against your source, by hand.",
          right_now_volunteer: "No number ships unchecked. Sit with them on this.",
          stuck: ["A number you can't verify? Cut it.", "Assistant changed a figure? Tell it to stop rounding."],
        },
      ],
    },
    "recurring-document": {
      id: "recurring-document",
      title: "Recurring document assistant",
      description: "Find the pattern in a document you write over and over, and turn it into something a colleague can run.",
      minutes: 28,
      tools: ["{assistant.name}"],
      needs_workspace: false,
      steps: [
        {
          title: "Name the document",
          participant_action: "Name the document you write over and over, the one that eats your week.",
          participant_example: "‘The weekly funder-facing program update email.’",
          volunteer_move: "Pin them to one document. Recurring means they will reuse this next week.",
          right_now_participant: "Name the document you write over and over.",
          right_now_volunteer: "Hold them to one repeated document.",
          stuck: ["Several? Pick the one you did most recently.", "Monthly still counts."],
        },
        {
          title: "Gather three examples",
          participant_action: "Gather three real past examples of that document.",
          participant_example: "Three past weekly updates you actually sent.",
          volunteer_move: "Three real ones, the pattern only shows across examples.",
          right_now_participant: "Gather three real past examples of it.",
          right_now_volunteer: "Three examples minimum, or the pattern will not show.",
          stuck: ["Only two? Use two, note it is thin.", "Different lengths are fine."],
          structure_only: {
            participant_action: "Redact names from the three examples before pasting, the voice lives in the phrasing, not the names.",
            participant_example: "Replace ‘Ms. Rivera’ with ‘[teacher]’, keep every other word.",
            volunteer_move: "Coach them to black out names and keep the sentences intact.",
            stuck: ["Replace names with [role] tags.", "Leave the phrasing exactly as written."],
          },
        },
        {
          title: "Find the pattern",
          participant_action: "Ask the assistant what pattern it sees across the three examples, structure, order, recurring phrases.",
          participant_example: "‘They all open with a highlight, then three updates, then an ask.’",
          volunteer_move: "Have them confirm the pattern matches how they actually think about it.",
          right_now_participant: "Ask what pattern it sees across the three.",
          right_now_volunteer: "Check the named pattern matches their real habit.",
          stuck: ["Vague? Ask for the shared structure as a numbered list.", "Wrong pattern? Add a fourth example."],
        },
        {
          title: "Write the instruction",
          participant_action: "Write the generation instruction in R-T-C-F, using the pattern as the Format.",
          participant_example: "Format: ‘Highlight line, three updates, one ask, in our voice.’",
          volunteer_move: "The pattern becomes the Format. That is the whole trick.",
          right_now_participant: "Write the R-T-C-F instruction with the pattern as Format.",
          right_now_volunteer: "Make the pattern the Format field, explicitly.",
          stuck: ["Format too loose? Paste the numbered pattern in directly.", "Name the voice in Context."],
        },
        {
          title: "Test on a fourth case",
          participant_action: "Test the instruction on a fourth real case and compare it to how you would have written it.",
          participant_example: "Feed this week's raw notes; see if the draft matches your usual.",
          volunteer_move: "Judge it against their own version, not against ‘good’.",
          right_now_participant: "Test on a fourth real case and compare.",
          right_now_volunteer: "Compare to their version, not to generic quality.",
          stuck: ["Off? Tell it which part broke the pattern.", "Close? Fix one thing and save."],
        },
        {
          title: "Save it for a colleague",
          participant_action: "Save it as a {assistant.project_noun} a colleague could run without you. Optionally build it into a small app surface later.",
          participant_example: "A saved {assistant.project_noun} named ‘Weekly Update Generator’.",
          volunteer_move: "Have them imagine handing it to a new hire. Does it stand alone?",
          right_now_participant: "Save it as a {assistant.project_noun} a colleague can run.",
          right_now_volunteer: "Test the hand-off: could a new hire run it?",
          stuck: ["Add a one-line ‘how to use’ note.", "Name it for the task, not the tool."],
        },
      ],
    },
    "multi-audience-rewriter": {
      id: "multi-audience-rewriter",
      title: "Multi-audience rewriter",
      description: "One message, four versions in one pass, same facts, framed for each reader.",
      minutes: 25,
      tools: ["{assistant.name}"],
      needs_workspace: false,
      steps: [
        {
          title: "Paste the source message",
          participant_action: "Paste the one source message you need to send to several audiences.",
          participant_example: "‘Our summer program is full; here is what happens next.’",
          volunteer_move: "One source message. The facts must stay identical across versions.",
          right_now_participant: "Paste the one source message.",
          right_now_volunteer: "Confirm it is a single message with fixed facts.",
          stuck: ["Long? Trim to the core facts first.", "Multiple messages? Do one at a time."],
        },
        {
          title: "Define four audiences",
          participant_action: "Define each of your four audiences in one line, including what each one cares about.",
          participant_example: "Board: outcomes and risk. Funder: impact and spend. Families: their child. The second language: same as families, in that language.",
          volunteer_move: "Push for what each audience cares about, not just who they are.",
          right_now_participant: "Define each audience in one line: who and what they care about.",
          right_now_volunteer: "Ask ‘what does each one care about?’, that drives the rewrite.",
          stuck: ["Stuck? Name the one question each audience will ask.", "Keep each to a single line."],
        },
        {
          title: "Write the one-pass prompt",
          participant_action: "Write an R-T-C-F prompt that produces all four versions in one pass, keeping the facts identical.",
          participant_example: "Task: ‘Produce four labelled versions of this message for these four audiences.’",
          volunteer_move: "Insist facts stay identical, only framing changes per audience.",
          right_now_participant: "Write a prompt producing all four versions in one pass.",
          right_now_volunteer: "Facts identical, framing different, hold that line.",
          stuck: ["Drifting facts? Add ‘do not change any facts’.", "List the four audiences in the Task."],
        },
        {
          title: "Generate",
          participant_action: "Generate all four versions and skim each for that audience's concern.",
          participant_example: "Four labelled blocks: Board, Funder, Families, The second language.",
          volunteer_move: "Have them check each version answers that audience's question.",
          right_now_participant: "Generate, then skim each version for its audience.",
          right_now_volunteer: "Each version must answer its audience's question.",
          stuck: ["Too similar? Ask it to differentiate the framing more.", "Missing one? Re-run naming all four."],
        },
        {
          title: "Check the second language",
          participant_action: "Check the second-language version with a fluent speaker at your table for tone, not just translation.",
          participant_example: "A colleague confirms it sounds natural to a family, not machine-translated.",
          volunteer_move: "If no fluent speaker is at the table, flag it for review, do not send it blind.",
          right_now_participant: "Check the second language with a fluent speaker at your table.",
          right_now_volunteer: "No fluent speaker? Flag it, do not ship it.",
          stuck: ["Stiff? Ask for a warmer, more natural register.", "Regional terms matter, ask your speaker."],
        },
        {
          title: "Note the biggest change",
          participant_action: "Note which audience needed the biggest change from the source. That is where your default voice fits worst.",
          participant_example: "‘The board version changed most, we bury risk.’",
          volunteer_move: "This note is the lesson: your natural voice suits one audience, not all.",
          right_now_participant: "Note which audience needed the biggest change.",
          right_now_volunteer: "Name the audience your default voice fits worst.",
          stuck: ["Unsure? Compare each to the source length.", "Write it as one sentence."],
        },
      ],
    },
    "spreadsheet-question": {
      id: "spreadsheet-question",
      title: "Spreadsheet question assistant",
      description: "Ask leadership's real questions of your data, on a made-up sheet, verified by hand.",
      minutes: 28,
      tools: ["{assistant.name}"],
      needs_workspace: false,
      steps: [
        {
          title: "Write the three questions",
          participant_action: "Write the three questions leadership actually asks about this data.",
          participant_example: "‘Which sites are under-enrolled? Where is attendance dropping? What did we spend per student?’",
          volunteer_move: "Real questions leadership asks, not questions the data happens to answer.",
          right_now_participant: "Write the three questions leadership actually asks.",
          right_now_volunteer: "Insist on their real questions, not convenient ones.",
          stuck: ["Too many? Pick the three most repeated.", "Phrase them as questions, not topics."],
        },
        {
          title: "Build a made-up sheet",
          participant_action: "Build a made-up sheet with the same column headings as your real one and about ten invented rows.",
          participant_example: "Columns: Site, Enrolled, Attendance%, Spend. Ten invented rows.",
          volunteer_move: "Same headings, invented data. The structure is what matters.",
          right_now_participant: "Build a made-up sheet: real headings, ten invented rows.",
          right_now_volunteer: "Check headings are real and every row is invented.",
          stuck: ["Copy your real headers, delete real rows.", "Ten rows is enough to test."],
          note: "This recipe is always structure-only. Never upload a real sheet at this event.",
        },
        {
          title: "Upload it",
          participant_action: "Upload your made-up sheet to the assistant.",
          participant_example: "Attach the ten-row file you just built.",
          volunteer_move: "Confirm it is the made-up file, not the real one, before uploading.",
          right_now_participant: "Upload the made-up sheet.",
          right_now_volunteer: "Double-check it is the invented file.",
          stuck: ["No upload? Paste the rows as text.", "Small files work best."],
        },
        {
          title: "Ask it to describe the sheet",
          participant_action: "Before asking anything, ask the assistant to describe what it sees in the sheet.",
          participant_example: "‘What columns are here and what does each seem to represent?’",
          volunteer_move: "This catches misread columns before they poison every answer.",
          right_now_participant: "Ask it to describe the sheet before you ask questions.",
          right_now_volunteer: "Make them verify it read the columns right first.",
          stuck: ["Wrong read? Rename an ambiguous header.", "Confirm units, dollars, percent."],
        },
        {
          title: "Ask your three questions",
          participant_action: "Ask your three questions one at a time, reading each answer before the next.",
          participant_example: "Q1 about under-enrolment, then Q2, then Q3.",
          volunteer_move: "One at a time. Batched questions get shallow answers.",
          right_now_participant: "Ask your three questions one at a time.",
          right_now_volunteer: "Keep them to one question at a time.",
          stuck: ["Vague answer? Ask it to show the rows behind it.", "Follow up before moving on."],
        },
        {
          title: "Verify two by hand",
          participant_action: "Verify two of the answers by hand against the sheet.",
          participant_example: "Count the under-enrolled sites yourself; compare.",
          volunteer_move: "Trust is earned per answer. Have them check two, minimum.",
          right_now_participant: "Verify two answers by hand against the sheet.",
          right_now_volunteer: "Sit with them and hand-check two answers.",
          stuck: ["Mismatch? The question was ambiguous, refine it.", "Ask it to show its working."],
        },
      ],
    },
    "month-end-pack": {
      id: "month-end-pack",
      title: "Month-end documentation pack",
      description: "Turn the email you chase people with into a template and checklist that get complete submissions.",
      minutes: 28,
      tools: ["{assistant.name}"],
      needs_workspace: false,
      steps: [
        {
          title: "List what complete means",
          participant_action: "List everything a complete submission must contain. Be specific, this list becomes the checklist.",
          participant_example: "‘Signed timesheet, receipts over $25, mileage log, one-line narrative.’",
          volunteer_move: "Push for the full list. Every missing item is a chase email later.",
          right_now_participant: "List everything a complete submission must contain.",
          right_now_volunteer: "Get the whole list, gaps become chase emails.",
          stuck: ["Unsure? List what is usually missing when people submit.", "Be specific about formats and thresholds."],
        },
        {
          title: "Paste your current email",
          participant_action: "Paste the email you currently send to chase these submissions.",
          participant_example: "‘Friendly reminder that month-end is due Friday…’",
          volunteer_move: "Paste the real one, its weaknesses are the lesson.",
          right_now_participant: "Paste the email you currently send.",
          right_now_volunteer: "Use the real chase email, warts and all.",
          stuck: ["No template? Paste your last reminder.", "Include the subject line."],
        },
        {
          title: "Ask why it comes back incomplete",
          participant_action: "Ask the assistant why incomplete submissions happen, given your list and your email.",
          participant_example: "‘What in this email makes people forget the mileage log?’",
          volunteer_move: "Have them look for what the email does not make obvious.",
          right_now_participant: "Ask why incomplete submissions happen.",
          right_now_volunteer: "Point them at what the current email hides.",
          stuck: ["Generic answer? Ask it to tie each gap to a line in the email.", "Focus on the most-missed item."],
        },
        {
          title: "Write the template + checklist prompt",
          participant_action: "Write an R-T-C-F prompt producing a request template plus a completeness checklist.",
          participant_example: "Format: ‘A short email with a numbered checklist and one deadline.’",
          volunteer_move: "Make sure the checklist mirrors the list from step 1 exactly.",
          right_now_participant: "Write the prompt for a template plus a completeness checklist.",
          right_now_volunteer: "Checklist must match the step-1 list, item for item.",
          stuck: ["Checklist drifting? Paste your list into the Task.", "Keep the email short."],
        },
        {
          title: "Generate",
          participant_action: "Generate the template and checklist, then read it as if you were the person submitting.",
          participant_example: "An email with a subject, a four-item checklist, and a bold deadline.",
          volunteer_move: "Have them check it is skimmable in ten seconds.",
          right_now_participant: "Generate, then read it as the person submitting.",
          right_now_volunteer: "Is it skimmable in ten seconds? Make them check.",
          stuck: ["Too long? Cut to the checklist and deadline.", "Buried deadline? Move it up and bold it."],
        },
        {
          title: "Optionally build a form",
          participant_action: "Optionally, rebuild the template as a form in your own workspace so submissions arrive complete and structured.",
          participant_example: "A Google Form or Microsoft Form with one required field per checklist item.",
          volunteer_move: "Only if there is time. The email alone is a real win.",
          right_now_participant: "Optionally rebuild it as a form in your workspace.",
          right_now_volunteer: "Time-boxed, the email is enough if you are short.",
          stuck: ["Short on time? Skip the form; keep the email.", "Make each checklist item a required field."],
        },
      ],
    },
    "workflow-automation": {
      id: "workflow-automation",
      title: "Workflow automation in your own workspace",
      description: "Automate the smallest useful thing, one trigger, one action, inside the workspace you already use.",
      minutes: 28,
      tools: ["{assistant.name}"],
      needs_workspace: true,
      steps: [
        {
          title: "Choose your workspace",
          participant_action: "Tell us which workspace your organization uses. This sets the build surfaces for every step that follows.",
          participant_example: "Most teams here use Google Workspace or Microsoft 365.",
          volunteer_move: "Confirm it is the workspace they can actually build in at the office.",
          right_now_participant: "Choose the workspace your organization uses.",
          right_now_volunteer: "Pick the one they can build in for real.",
          stuck: ["Both? Choose the one you use most.", "Not sure? Ask what your email runs on."],
        },
        {
          title: "Write the manual sequence",
          participant_action: "Write the manual sequence on screen as trigger, action, end, exactly how you do it by hand today in {workspace.name}.",
          participant_example: "Trigger: a form is submitted. Action: copy it to a sheet and email the lead. End: lead replies.",
          volunteer_move: "Make them write every click. Hidden steps are where automation breaks.",
          right_now_participant: "Write the manual sequence: trigger, action, end.",
          right_now_volunteer: "Get every click on screen, no hidden steps.",
          stuck: ["Too many steps? You will cut them next.", "Start at the very first trigger."],
        },
        {
          title: "Cut to one trigger, one action",
          participant_action: "Cut the sequence to a single trigger and a single action. Automate the smallest useful thing first.",
          participant_example: "Trigger: form submitted → Action: append a row to the sheet.",
          volunteer_move: "Resist the big automation. One trigger, one action, working, beats a grand plan.",
          right_now_participant: "Cut it to one trigger and one action.",
          right_now_volunteer: "Hold them to the smallest useful automation.",
          stuck: ["Can't cut? Pick the step that wastes the most time.", "The rest can come later."],
        },
        {
          title: "Ask for the script or flow",
          participant_action: "Ask the assistant to write the script or flow for {workspace.name}, using {workspace.surfaces}.",
          participant_example: "For {workspace.name}: a step-by-step build using {workspace.surfaces}.",
          volunteer_move: "Have them tell it exactly which workspace, so the steps match what they will see.",
          right_now_participant: "Ask it to write the flow for {workspace.name}.",
          right_now_volunteer: "Make sure it targets your workspace's actual tools.",
          stuck: ["Generic steps? Name the exact tool from {workspace.surfaces}.", "Ask for numbered clicks."],
        },
        {
          title: "Build it with your volunteer",
          participant_action: "Build it in {workspace.name} with your volunteer beside you, following the steps using {workspace.surfaces}.",
          participant_example: "Create the automation in {workspace.surfaces} and save it.",
          volunteer_move: "Sit beside them, hands off the keyboard. They must be able to redo it Monday.",
          right_now_participant: "Build it in {workspace.name} with your volunteer beside you.",
          right_now_volunteer: "Guide, do not touch. They rebuild this Monday, not you.",
          stuck: ["Stuck on a step? Ask the assistant to expand that one click.", "Permissions error? Ask your workspace admin later."],
        },
        {
          title: "Test on a made-up record",
          participant_action: "Test the automation on one made-up record and watch it run end to end.",
          participant_example: "Submit a fake form entry; confirm the row appears and the email sends.",
          volunteer_move: "Watch the whole run once. Half-tested automations fail silently.",
          right_now_participant: "Test on one made-up record, end to end.",
          right_now_volunteer: "Watch the full run, no silent half-tests.",
          stuck: ["Didn't fire? Check the trigger conditions.", "Fired twice? Add a stop condition."],
          warning: "Never test on a live list. Use a made-up record only.",
        },
        {
          title: "Save it and name a second editor",
          participant_action: "Save the automation and note who else can edit it, so it does not die when one person leaves.",
          participant_example: "‘Owned by Operations; the director has edit access.’",
          volunteer_move: "A bus-factor of one is a future outage. Get a second editor named.",
          right_now_participant: "Save it and note who else can edit it.",
          right_now_volunteer: "Insist on a second named editor.",
          stuck: ["Only you can edit? Share it with one colleague.", "Write down where it lives."],
        },
      ],
    },
    "ai-use-policy": {
      id: "ai-use-policy",
      title: "Responsible AI use policy",
      description: "A first-draft, one-page policy plus a plain-language staff summary and a red-lines list.",
      minutes: 25,
      tools: ["{assistant.name}"],
      needs_workspace: false,
      card_note: "Only if your team did not already do this in Prompt School.",
      steps: [
        {
          title: "Complete the intake",
          participant_action: "Complete the intake, the same fields as the Prompt School policy challenge: mission, jurisdiction, who you serve, tools, data types, red lines.",
          participant_example: "Reuse what you wrote in Prompt School if you did that challenge.",
          volunteer_move: "If they did this in Prompt School, paste it in and move fast.",
          right_now_participant: "Complete the policy intake fields.",
          right_now_volunteer: "Reuse their Prompt School inputs if they have them.",
          stuck: ["Did this earlier? Paste it and skip ahead.", "Types of data only, no real data."],
        },
        {
          title: "Generate the draft",
          participant_action: "Generate the first draft of the policy from your intake.",
          participant_example: "A one-page policy: purpose, allowed uses, red lines, who to ask.",
          volunteer_move: "Do not let them wordsmith yet. Get the whole draft first.",
          right_now_participant: "Generate the first draft from your intake.",
          right_now_volunteer: "Whole draft first, wordsmithing later.",
          stuck: ["Too long? Ask for one page.", "Too vague? Add your red lines to the prompt."],
        },
        {
          title: "Critique with three questions",
          participant_action: "Critique the draft with three questions: what is missing, what is unenforceable, what would surprise your staff.",
          participant_example: "‘Does this say who approves a new AI tool?’",
          volunteer_move: "Have them answer honestly. A policy nobody can follow protects no one.",
          right_now_participant: "Critique the draft: missing, unenforceable, surprising.",
          right_now_volunteer: "Push for honest answers, not polish.",
          stuck: ["Nothing missing? Ask a skeptical colleague's view.", "Check: approval, disclosure, data."],
        },
        {
          title: "Fix one thing",
          participant_action: "Fix the single most important weakness you found. One real fix beats ten cosmetic edits.",
          participant_example: "Add an approval step for adopting any new AI tool.",
          volunteer_move: "One fix. Depth over breadth in the time you have.",
          right_now_participant: "Fix the single most important weakness.",
          right_now_volunteer: "One real fix, not ten cosmetic ones.",
          stuck: ["Many issues? Fix the one with the biggest risk.", "Keep the rest as a to-do list."],
        },
        {
          title: "Produce a staff summary",
          participant_action: "Produce a one-page staff summary in plain language, what this means for someone's daily work.",
          participant_example: "‘You can use AI for drafting; never paste student records.’",
          volunteer_move: "Ask if a new hire would understand it. That is the bar.",
          right_now_participant: "Produce a one-page plain-language staff summary.",
          right_now_volunteer: "Test it against a new hire's understanding.",
          stuck: ["Jargon creeping in? Ask for sixth-grade reading level.", "Lead with what they can do."],
        },
        {
          title: "List what never goes in",
          participant_action: "Produce the list of what never goes into an AI tool, the red lines, as a standalone list staff can pin up.",
          participant_example: "‘Never: student names, family contacts, donor records, payroll.’",
          volunteer_move: "This list is the thing they will actually remember. Make it blunt.",
          right_now_participant: "Produce the standalone ‘never goes in’ list.",
          right_now_volunteer: "Make the red-line list blunt and postable.",
          stuck: ["Too soft? Use ‘never’, not ‘avoid’.", "Keep it to one screen."],
          callout: "You are leaving with a first draft for internal review, not an adopted policy.",
        },
      ],
    },
  },

  // Mentor Resources, always-available coaching content, editable per event.
  mentors: {
    one_rule:
      "Coach, do not drive. The nonprofit representative types, decides, and owns the result. You ask the questions. What is obvious to you is brand new here, so say the obvious thing. The tool they build is the artifact. The confidence they leave with is the gift.",
    build_help: {
      title: "Helping your table pick a recipe",
      points: [
        "The platform will not choose for them. You will. All nine recipes are on the screen as options, and your job is to have the conversation that lands on one.",
        "Start with the sentence, not the list. Get them to finish 'we are building blank, for blank, and done looks like blank' out loud before anyone opens a recipe. If they cannot finish it, the recipe is not the problem yet.",
        "Push hardest on the middle blank. 'For the team' is not an answer. A named person and a moment when they would use it is an answer.",
        "The third blank is your protection against a table spending the whole build on something they cannot show anyone. If they cannot say what done looks like in fifteen seconds, narrow it until they can.",
        "Four questions that get a table to the right recipe: What shape is the thing, a document, something a colleague could use without you, a sequence that runs itself, or an answer out of data you already have? Is it about money and funders, operations, voice and audience, programs, or governance? Does it touch real names or records? And what would make this worth the afternoon?",
        "If the table is stuck between two recipes, pick the smaller one. If they are stuck across all of them, open the organization knowledge base. It always works and everything they do in it is reusable.",
      ],
    },
    data_rule_note:
      "The platform will not ask them whether their project touches real records. You do. Ask early, and if the answer is yes, build the same thing on a made-up example with the same fields.",
    room: {
      title: "What to expect in the room",
      snapshot: null,
      points: [
        "Most nonprofit attendees will rate their AI confidence low, and a good number will tell you they are hesitant about using AI at all. That hesitation is usually a considered position rather than a knowledge gap, and it often comes from the most senior people present. Do not treat it as something to argue away.",
        "The biggest time sink in nonprofit work is almost always administrative: paperwork, reconciliation, records, rosters, data cleanup. The second is fundraising and reporting, which is the highest-stakes writing they do.",
        "People rarely name social media as their real problem, even when they tick it on a form. Follow what they say eats their week.",
        "Many organizations serve communities in more than one language, so translation and register-shifting come up often.",
        "The two things you will hear most: they want help with writing that does not cost them their own voice, and they want a way to use AI that does not feel ethically wrong to them.",
      ],
      closer:
        "If somebody at your table pushes back on AI today, that is not a problem to manage. That is the most interesting conversation in the room.",
    },
    roles: [
      { name: "Prompt Coach", body: "Asks the R-T-C-F questions and keeps the representative in the driver's seat. Never take the keyboard." },
      { name: "Output Reviewer", body: "Reads every output for three things. Is it accurate, does it contain anything private, does it sound like the organization. You are the one volunteer explicitly allowed to say \u201Cdo not send that\u201D." },
      { name: "Note Taker", body: "Saves prompts and outputs into the representative's {assistant.project_noun} as they are made, so nothing is lost at the end of the day." },
      { name: "Session Expert", body: "Floats across tables for tool-specific and technical questions including workspace automation. Assigned at sign-up, not table-bound." },
    ],
    allocation: [
      { ratio: "Three to one", guidance: "One role each; session experts excluded." },
      { ratio: "Two to one", guidance: "One takes Prompt Coach; the other takes Output Reviewer and Note Taker." },
      { ratio: "One to one", guidance: "That volunteer covers all three table roles and leans on a session expert." },
      { ratio: "Four or more to one", guidance: "Pair up on an existing role rather than inventing a fifth. Two can share Output Reviewer usefully. Do not add a fourth voice." },
    ],
    segment_moves: [
      { segment: "Prompt School, teaching", move: "Let them read the four elements first. Do not explain over it." },
      { segment: "Prompt School, the scored challenge", move: "Ask which of the four elements is thinnest. Nine times out of ten it is Context. Do not fill it in for them." },
      { segment: "Prompt School, choose and reflect", move: "Help them pick one real challenge, then ask the three reflection questions out loud." },
      { segment: "The Build, scoping", move: "Hold them to one person and one fifteen-second demo. Do not type." },
      { segment: "The Build, building", move: "Watch for stuck moments and coach the question, not the answer. Keep your hands off the keyboard." },
    ],
    escalation: {
      steps: [
        "The step's own Stuck panel",
        "Your own judgment",
        "The volunteer beside you",
        "A session expert",
      ],
      closer: "There are thirty of you. That is more support than any event staff could be.",
    },
    recipe_notes: {
      "organization-knowledge-base": { trap: "They dump everything in and it becomes a junk drawer.", question: "If a new hire read only this, what are the five things they must know?" },
      "grant-proposal-drafting": { body: "The trap is letting the assistant write prose too early. Steps 2 and 3 feel like preamble and they are where the entire quality of the output is decided. If the draft comes back generic, do not fix the draft. Go back and find the fact from step 2 that never made it into the prompt." },
      "funder-report": { trap: "They trust a number the AI produced.", question: "Where did that figure come from, and can you point to it in your source?" },
      "recurring-document": { trap: "They describe the document instead of pasting real examples.", question: "What do all three past versions actually have in common?" },
      "multi-audience-rewriter": { trap: "The four versions drift apart on facts.", question: "Are the facts identical across all four, or did one change?" },
      "spreadsheet-question": { trap: "They ask before checking the AI read the columns right.", question: "Did it describe your columns correctly before you asked anything?" },
      "month-end-pack": { trap: "They rewrite the email but never define what complete means.", question: "What exactly does a complete submission contain?" },
      "workflow-automation": { trap: "They try to automate the whole chain at once.", question: "What is the single smallest step worth automating first?" },
      "ai-use-policy": { trap: "They aim for a finished policy in twenty minutes.", question: "What are the three red lines you are certain about right now?" },
    },
    fifteen_second_test:
      "Partway through the build, ask your table honestly: could we demonstrate a real, working piece of this in fifteen seconds, right now? If the answer is no, suggest switching to the organization knowledge base recipe. Fifteen minutes, it always works, and everything already built stays saved. Say it the way it is meant: let's bank a win. Never frame it as failure.",
  },

  // The Sustainable Stack, takeaway reading, meant to be read alone later.
  stack: {
    eyebrow: "TAKE IT HOME",
    title: "The Sustainable Stack",
    subtitle:
      "What you keep after today, what it costs (almost nothing), and how to unlock the rest of the nonprofit internet.",

    workspace_title: "Your workspace, what you have and until when",
    workspace_unsponsored:
      "Everything you built today lives in your organization's own {assistant.name} {assistant.project_noun} and in your own Google or Microsoft workspace. It is yours. The free tier of {assistant.name} is enough to keep using all of it, and the rest of this page shows you how to pay less if you ever outgrow it.",
    workspace_sponsored_rules: [
      "seats belong to your organization (org emails, not personal Gmail)",
      "your tool steward owns invites and renewals",
      "before the period ends, your org makes one decision per tool: free tier or nonprofit rate",
    ],
    workspace_sponsored_closer: "The rest of this page makes that decision easy.",

    goodstack_title: "Goodstack, verify once, unlock the nonprofit internet",
    goodstack_what:
      "What it is. Goodstack (formerly Percent) is the verification infrastructure the world's biggest tech companies use to confirm that an organization is a genuine nonprofit. Its database covers 9 million plus organizations across 240 plus countries, and it is the engine behind Google for Nonprofits, Anthropic's Claude for Nonprofits, and nonprofit programs from companies like Canva, monday.com, and Asana.",
    goodstack_why:
      "Why it matters to you. Companies want to give nonprofits discounted or free tools, but they need proof an org is real. Goodstack is that proof, and once you are verified through one partner program, the same verification identity opens others. For this room, the headline door it opens: Claude Team at up to 75 percent off, around 8 dollars per user per month.",
    goodstack_cost:
      "Does it cost money? No. Verification through a partner program costs your organization nothing. Goodstack charges the companies running the programs, not the causes applying to them. If anyone ever asks your nonprofit to pay for verification, that is a red flag, not a fee.",
    goodstack_checks_intro:
      "What the verification checks. Four things, per Goodstack's published process.",
    goodstack_checks: [
      "Nonprofit status. You are recognized as a charitable organization by the regulator in your country. For US organizations, your 501(c)(3) recognition by the IRS is exactly this.",
      "Not-for-profit operation and public benefit. What your founding documents and IRS determination already establish.",
      "Applicant association. Proof that you actually belong to the org. Goodstack does this primarily by domain matching: your application email's domain should match your organization's website domain. If it does not (for example, you applied from a personal Gmail), they contact the organization to confirm you. Slower, but it works.",
      "Good standing. Your org is in compliance with its regulator.",
    ],
    goodstack_keep_handy_label: "WHAT TO KEEP HANDY (US ORGS)",
    goodstack_keep_handy:
      "Your IRS determination letter, your EIN, your website URL, and an org-domain email address for the applicant. Note: each partner (Anthropic, Google, and so on) sets its own eligibility rules on top of the base verification, so approval into one program does not automatically guarantee another, but the heavy lifting carries over.",
    goodstack_how_long:
      "How long it takes. Goodstack advertises verification in hours to days, not weeks. The practical variable is the applicant-association check, which is near-instant with a matching org domain and slower via manual confirmation without one.",
    goodstack_how_to_intro: "How to actually do it (the path for this room).",
    goodstack_how_to_steps: [
      "Go to Claude's nonprofit page (claude.com, Claude for Nonprofits) and start the application; it routes you into Goodstack's verification flow.",
      "Apply using your org-domain email if you have one.",
      "Upload or enter your registration details when asked.",
      "Wait for the confirmation email; then your workspace billing can move to the nonprofit rate whenever you choose to upgrade.",
      "Stuck at any step? Goodstack runs its own applicant support inside the verification flow, or ask your {assistant.name} workspace to explain the step you are on. It is rather good at that.",
    ],
    goodstack_caveat:
      "One honest caveat: final eligibility is always the partner's call at verification time. That is precisely why your steward should start the process soon rather than the week you need it.",
    goodstack_domain_note:
      "(No org-domain email? That is worth fixing regardless. Google Workspace for Nonprofits gives your org professional email on your own domain, free, unlocked, fittingly, by the same Goodstack verification.)",

    free_stack_title: "The 0 dollar US stack (claim this quarter)",
    free_stack_columns: ["Offer", "What", "Where to claim"],
    free_stack_rows: [
      {
        offer: "Google for Nonprofits",
        what: "Google Workspace for Nonprofits free, plus 10,000 dollars per month in Google Ad Grants",
        where: "google.com/nonprofits, verified through Goodstack",
      },
      {
        offer: "Microsoft 365 Business Basic",
        what: "Free for up to 300 users: web Office apps, Teams, business email, 1 TB OneDrive each. Note: the old free Business Premium and E1 grants ended in July 2025; Business Premium is now about 75 percent off instead",
        where: "Microsoft's nonprofit portal",
      },
      {
        offer: "Canva for Nonprofits",
        what: "Canva Pro free for eligible nonprofits",
        where: "canva.com/canva-for-nonprofits",
      },
      {
        offer: "Slack for Nonprofits",
        what: "Free Pro plan for workspaces under 250 people",
        where: "slack.com, nonprofit program",
      },
      {
        offer: "Claude for Nonprofits",
        what: "Up to 75 percent off Claude Team, around 8 dollars per user per month",
        where: "claude.com, Claude for Nonprofits",
      },
    ],
    free_stack_floor:
      "Free-forever floor beneath all of it: the {assistant.name} free tier, the Gemini free tier, Google AI Studio, and script.google.com. Your prompts and skills are portable. The R-T-C-F framework works in every one of these tools.",
    free_stack_offers_note:
      "Offers current as of September 2026. Terms change, so confirm on the linked page before you commit to anything.",
    rule_label: "RULE BEFORE PAYING FOR ANYTHING",
    rule: "A free tier must block you three times in one week. Until then, you do not need the upgrade.",

    owns_title: "Who owns this",
    steward_prompt: "Name your tool steward",
    steward_line: "An unowned stack is an abandoned stack.",

    challenge_label: "THE 48-HOUR CHALLENGE",
    challenge:
      "Within two days: pick one real task, apply R-T-C-F, save the prompt to your library. Ten minutes saved this week becomes an hour saved every week by next month. That is the whole game.",
  },

  // Interface copy (not event data). Editable here so nothing is hard-coded.
  ui: {
    footer: { not_you: "Not you?" },
    area_coming_soon: "This section is coming soon.",
    part3: {
      toggle_own: "Work with my own volunteers",
      toggle_team: "Work as a whole organization team",
      team_name_label: "Team name",
      driving_question: "Which laptop is driving?",
      driving_yes: "This is the driving laptop",
      driving_no: "Another laptop is driving",
      standby_line:
        "Your team's draft is on your table's driving laptop. Look up, not down.",
      choose_label: "Choose your challenge",
      change: "Change challenge",
      generate: "Generate my prompt",
      copy: "Copy prompt",
      stuck_label: "Stuck?",
      scope_label: "What a win looks like in 10 minutes",
      partial_label: "Partial win",
      mark_done: "Mark done",
      done: "Done",
    },
  },
};

// ---- Derived helpers ------------------------------------------------------

// Navigation follows the `order` field. Full area fields are carried through
// (timers, always_available, comes_after).
export const navItems = [...config.areas]
  .sort((a, b) => a.order - b.order)
  .map((a) => ({
    ...a,
    slug: a.id.replace(/_/g, "-"),
    label: config.area_labels[a.id],
  }));

export const firstAreaSlug = navItems[0].slug;

export function getAreaBySlug(slug) {
  return navItems.find((n) => n.slug === slug) || null;
}

// Readable event date derived from event_date (plain calendar date, no shift).
export const eventDateDisplay = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
}).format(new Date(config.event_date));

// ---- Token rendering ------------------------------------------------------
// Screen content references tools via tokens like {assistant.name} or
// {assistant.project_noun}. Resolve them against config at render time.

const tokenContext = {
  ...config,
  assistant: config.tools.assistant,
};

function resolvePath(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
}

export function renderTokens(str) {
  if (typeof str !== "string") return str;
  return str.replace(/\{([^}]+)\}/g, (match, path) => {
    const value = resolvePath(tokenContext, path.trim());
    return value == null ? match : String(value);
  });
}
